import {
  useEffect,
  useContext,
  useCallback,
  createContext,
  type FC,
  type PropsWithChildren,
} from 'react';
import {
  Box
} from '@mui/joy';
import {
  useModalQueryParam,
  useQueryParamModalVisibility
} from '@shared/lib';
import {
  useSessionSelector,
  selectFasttrackJwt,
  selectIsAuthenticated,
} from '@entities/session';
import {
  FASTTRACK_BRAND,
  FASTTRACK_SOURCE,
  FASTTRACK_STAGING,
  FASTTRACK_REWARDS_ENABLE,
  FASTTRACK_INTEGRATION_VERSION,
  PLAYER_REWARDS_TOGGLE_EVENT,
  FASTTRACK_INITIALIZED_CONSOLE_MESSAGE,
} from '../config';
import {
  loadFasttrackScript,
  cleanupExistingFastTrackElements,
  useInterceptConsoleMessage
} from '../lib';
import {
  OverriddenRewardPopupStyles
} from './overridden-reward-popup-styles.component';

export interface TogglePlayerRewards {
  login(): void;
  logout(): void;
};

const ModernTogglePlayerRewardsContext = createContext<Maybe<TogglePlayerRewards>>(null);

export const useTogglePlayerRewards = (): TogglePlayerRewards => {
  const context = useContext(ModernTogglePlayerRewardsContext);
  if (!context) throw new Error('useModernTogglePlayerRewards must be used within a TogglePlayerRewardsProvider');
  return context;
};

export const TogglePlayerRewardsProvider: FC<PropsWithChildren> = ({ children }) => {
  const modalQueryParam = useModalQueryParam();
  const openRewardsModal = useQueryParamModalVisibility('rewards');

  const fasttrackJwt = useSessionSelector(selectFasttrackJwt);
  const isAuthenticated = useSessionSelector(selectIsAuthenticated);

  const handleLogin = useCallback(() => {
    window.sid = fasttrackJwt
  }, []);

  const handleLogout = useCallback(() => {
    window.sid = undefined;
    window.FasttrackCrm = undefined;
    cleanupExistingFastTrackElements();
  }, []);

  const handleToggleRewards = useCallback(() => {
    const toggleElement = document.getElementById('ft-my-rewards-toggle-element');
    if (toggleElement) toggleElement.click();
    else console.warn('Toggle element not found');
  }, []);

  useInterceptConsoleMessage(() => {
    if (openRewardsModal) setTimeout(handleToggleRewards, 1000);
  }, FASTTRACK_INITIALIZED_CONSOLE_MESSAGE);

  useEffect(() => {
    cleanupExistingFastTrackElements();

    if (isAuthenticated) {
      loadFasttrackScript(() => {
        window.sid = fasttrackJwt;
        window.source = FASTTRACK_SOURCE;
        window.fasttrackbrand = FASTTRACK_BRAND;
        window.fasttrack = {
          autoInit: true,
          enableJWT: true,
          staging: FASTTRACK_STAGING,
          integrationVersion: FASTTRACK_INTEGRATION_VERSION,
          rewards: { enable: FASTTRACK_REWARDS_ENABLE },
          inbox: { enable: true, badgeId: '#ft-crm-inbox-badge' },
        };

        try {
          if (!window.FasttrackCrm?._isMounted) new window.FastTrackLoader();
        } catch (error) {
          console.error('FastTrack loader execution error:', error);
        }
      });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const handlerPlayerRewardsPopupToggle = () => modalQueryParam.openModal('rewards');
    document.addEventListener(PLAYER_REWARDS_TOGGLE_EVENT, handlerPlayerRewardsPopupToggle);
    return () => {
      document.removeEventListener(PLAYER_REWARDS_TOGGLE_EVENT, handlerPlayerRewardsPopupToggle);
    };
  }, []);

  useEffect(() => {
    if (openRewardsModal) handleToggleRewards();

    const handleClose = (event: MouseEvent): void => {
      const target = event.target as Maybe<HTMLButtonElement>;
      if (target?.nodeName === 'FT-MY-REWARDS') modalQueryParam.closeModal('rewards');
    };

    document.addEventListener('click', handleClose);
    return () => {
      document.removeEventListener('click', handleClose);
    };
  }, [openRewardsModal, window.FasttrackCrm?._isMounted]);

  return (
    <ModernTogglePlayerRewardsContext.Provider
      value={{
        login: handleLogin,
        logout: handleLogout,
      }}>
      {children}
      <OverriddenRewardPopupStyles />
      <ft-my-rewards-toggle>
        <Box
          id='ft-my-rewards-toggle-element'
          component='div'
          sx={{
            visibility: 'hidden'
          }}
        />
      </ft-my-rewards-toggle>
    </ModernTogglePlayerRewardsContext.Provider>
  );
};